import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  type CompanyIntentErrors,
  type CompanyIntentValue,
  type IntentOption,
  OBJECTIVE_OTHER,
  optionLabel,
  toOptions,
} from "../../lib/companyIntent";
import { countryLabel, countryOptions, regionLabel, regionOptions } from "../../lib/regionOptions";
import { useCompaniesIntentOptionsRetrieve } from "../../services/companies/generated/companies/companies";
import "./CompanyIntentFields.css";

export interface IntentOptions {
  corridorSides: IntentOption[];
  tradeRoles: IntentOption[];
  companySizes: IntentOption[];
  objectives: IntentOption[];
  timelines: IntentOption[];
}

/** Option lists for the corridor/intent fields (backend configuration). */
export function useIntentOptions(): { options: IntentOptions; isLoading: boolean; isError: boolean } {
  const { data, isLoading, isError } = useCompaniesIntentOptionsRetrieve({
    query: { staleTime: 60 * 60 * 1000 },
  });
  const options = useMemo<IntentOptions>(
    () => ({
      corridorSides: toOptions(data?.corridorSides),
      tradeRoles: toOptions(data?.tradeRoles),
      companySizes: toOptions(data?.companySizes),
      objectives: toOptions(data?.objectives),
      timelines: toOptions(data?.timelines),
    }),
    [data],
  );
  return { options, isLoading, isError };
}

interface CompanyIntentFieldsProps {
  value: CompanyIntentValue;
  onChange: (next: CompanyIntentValue) => void;
  errors?: CompanyIntentErrors;
  /** Registration marks the mandatory fields with an asterisk. */
  required?: boolean;
  disabled?: boolean;
  idPrefix: string;
}

function toggle(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((v) => v !== item) : [...list, item];
}

/**
 * Corridor side, trade roles, size, objective, target market and timeline —
 * shared by the registration form and the company profile (WP1.5).
 */
const CompanyIntentFields = ({
  value,
  onChange,
  errors = {},
  required = false,
  disabled = false,
  idPrefix,
}: CompanyIntentFieldsProps) => {
  const { t } = useTranslation(["components", "common"]);
  const { options, isLoading, isError } = useIntentOptions();
  const set = <K extends keyof CompanyIntentValue>(key: K, v: CompanyIntentValue[K]) =>
    onChange({ ...value, [key]: v });
  const star = required ? <span className="intent-fields__required">*</span> : null;
  const id = (name: string) => `${idPrefix}-${name}`;

  const addableCountries = countryOptions().filter((c) => !value.targetCountries.includes(c.value));

  if (isError) {
    return (
      <p className="intent-fields__error" role="alert">
        {t("companyIntent.loadError")}
      </p>
    );
  }

  return (
    <div className="intent-fields" aria-busy={isLoading}>
      <div className="intent-fields__row">
        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("side")}>
            {t("companyIntent.sideLabel")} {star}
          </label>
          <select
            id={id("side")}
            className="intent-fields__control"
            value={value.corridorSide}
            onChange={(e) => set("corridorSide", e.target.value)}
            disabled={disabled}
          >
            <option value="">{t("companyIntent.select")}</option>
            {options.corridorSides.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.corridorSide && <p className="intent-fields__error">{errors.corridorSide}</p>}
        </div>

        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("size")}>
            {t("companyIntent.sizeLabel")} {star}
          </label>
          <select
            id={id("size")}
            className="intent-fields__control"
            value={value.companySize}
            onChange={(e) => set("companySize", e.target.value)}
            disabled={disabled}
          >
            <option value="">{t("companyIntent.select")}</option>
            {options.companySizes.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.companySize && <p className="intent-fields__error">{errors.companySize}</p>}
        </div>
      </div>

      <fieldset className="intent-fields__field intent-fields__fieldset">
        <legend className="intent-fields__label">
          {t("companyIntent.rolesLabel")} {star}
        </legend>
        <p className="intent-fields__hint">{t("companyIntent.rolesHint")}</p>
        <div className="intent-fields__chips">
          {options.tradeRoles.map((o) => (
            <label key={o.value} className="intent-fields__check">
              <input
                type="checkbox"
                checked={value.tradeRoles.includes(o.value)}
                onChange={() => set("tradeRoles", toggle(value.tradeRoles, o.value))}
                disabled={disabled}
              />
              {o.label}
            </label>
          ))}
        </div>
        {errors.tradeRoles && <p className="intent-fields__error">{errors.tradeRoles}</p>}
      </fieldset>

      <div className="intent-fields__row">
        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("objective")}>
            {t("companyIntent.objectiveLabel")} {star}
          </label>
          <select
            id={id("objective")}
            className="intent-fields__control"
            value={value.objective}
            onChange={(e) => set("objective", e.target.value)}
            disabled={disabled}
          >
            <option value="">{t("companyIntent.select")}</option>
            {options.objectives.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.objective && <p className="intent-fields__error">{errors.objective}</p>}
        </div>

        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("timeline")}>
            {t("companyIntent.timelineLabel")} {star}
          </label>
          <select
            id={id("timeline")}
            className="intent-fields__control"
            value={value.marketEntryTimeline}
            onChange={(e) => set("marketEntryTimeline", e.target.value)}
            disabled={disabled}
          >
            <option value="">{t("companyIntent.select")}</option>
            {options.timelines.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.marketEntryTimeline && <p className="intent-fields__error">{errors.marketEntryTimeline}</p>}
        </div>
      </div>

      <div className="intent-fields__field">
        <label className="intent-fields__label" htmlFor={id("objective-details")}>
          {t("companyIntent.objectiveDetailsLabel")}{" "}
          {value.objective === OBJECTIVE_OTHER ? (
            <span className="intent-fields__required">*</span>
          ) : (
            <span className="intent-fields__optional">{t("common:labels.optional")}</span>
          )}
        </label>
        <textarea
          id={id("objective-details")}
          className="intent-fields__control intent-fields__textarea"
          rows={2}
          maxLength={500}
          placeholder={t("companyIntent.objectiveDetailsPlaceholder")}
          value={value.objectiveDetails}
          onChange={(e) => set("objectiveDetails", e.target.value)}
          disabled={disabled}
        />
        {errors.objectiveDetails && <p className="intent-fields__error">{errors.objectiveDetails}</p>}
      </div>

      <fieldset className="intent-fields__field intent-fields__fieldset">
        <legend className="intent-fields__label">
          {t("companyIntent.targetRegionsLabel")}{" "}
          <span className="intent-fields__optional">{t("common:labels.optional")}</span>
        </legend>
        <div className="intent-fields__chips">
          {regionOptions().map((o) => (
            <label key={o.value} className="intent-fields__check">
              <input
                type="checkbox"
                checked={value.targetRegions.includes(o.value)}
                onChange={() => set("targetRegions", toggle(value.targetRegions, o.value))}
                disabled={disabled}
              />
              {o.label}
            </label>
          ))}
        </div>
        {errors.targetRegions && <p className="intent-fields__error">{errors.targetRegions}</p>}
      </fieldset>

      <div className="intent-fields__field">
        <label className="intent-fields__label" htmlFor={id("countries")}>
          {t("companyIntent.targetCountriesLabel")}{" "}
          <span className="intent-fields__optional">{t("common:labels.optional")}</span>
        </label>
        <select
          id={id("countries")}
          className="intent-fields__control"
          value=""
          onChange={(e) => {
            if (e.target.value) set("targetCountries", [...value.targetCountries, e.target.value]);
          }}
          disabled={disabled}
        >
          <option value="">{t("companyIntent.addCountry")}</option>
          {addableCountries.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {value.targetCountries.length > 0 && (
          <ul className="intent-fields__tags">
            {value.targetCountries.map((code) => (
              <li key={code} className="intent-fields__tag">
                {countryLabel(code)}
                <button
                  type="button"
                  className="intent-fields__tag-remove"
                  onClick={() =>
                    set(
                      "targetCountries",
                      value.targetCountries.filter((c) => c !== code),
                    )
                  }
                  disabled={disabled}
                  aria-label={t("companyIntent.removeCountry", { country: countryLabel(code) })}
                >
                  <i className="fa-solid fa-times" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        {errors.targetCountries && <p className="intent-fields__error">{errors.targetCountries}</p>}
      </div>
    </div>
  );
};

interface CompanyIntentSummaryProps {
  value: CompanyIntentValue;
}

/** Read-only view of the same fields (moderator company detail). */
export const CompanyIntentSummary = ({ value }: CompanyIntentSummaryProps) => {
  const { t } = useTranslation("components");
  const { options } = useIntentOptions();
  const dash = "—";
  const rows: Array<[string, string]> = [
    [t("companyIntent.summary.corridorSide"), optionLabel(options.corridorSides, value.corridorSide) || dash],
    [
      t("companyIntent.summary.tradeRoles"),
      value.tradeRoles.map((r) => optionLabel(options.tradeRoles, r)).join(", ") || dash,
    ],
    [t("companyIntent.summary.companySize"), optionLabel(options.companySizes, value.companySize) || dash],
    [t("companyIntent.summary.objective"), optionLabel(options.objectives, value.objective) || dash],
    [t("companyIntent.summary.objectiveDetails"), value.objectiveDetails || dash],
    [t("companyIntent.summary.targetRegions"), value.targetRegions.map((r) => regionLabel(r)).join(", ") || dash],
    [t("companyIntent.summary.targetCountries"), value.targetCountries.map((c) => countryLabel(c)).join(", ") || dash],
    [t("companyIntent.summary.timeline"), optionLabel(options.timelines, value.marketEntryTimeline) || dash],
  ];
  return (
    <dl className="intent-summary">
      {rows.map(([label, text]) => (
        <div key={label} className="intent-summary__row">
          <dt className="intent-summary__label">{label}</dt>
          <dd className="intent-summary__value">{text}</dd>
        </div>
      ))}
    </dl>
  );
};

export default CompanyIntentFields;
